import React, { useState, useMemo } from "react";
import { db, storage } from "../../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useAuth } from "../../AuthContext";
import { AMENITY_GROUPS, pillAllowed, usePillStates } from "../shared/pillCatalog";


export default function BarProfile() {
  const { user, userBars, refreshUserBars } = useAuth();

  const myBars = useMemo(
    () => userBars.filter((bar) => bar.ownerId === user?.uid),
    [userBars, user]
  );

  const [selectedBarId, setSelectedBarId] = useState("");
  const [phone, setPhone] = useState("");
  const [amenities, setAmenities] = useState([]);
  const [customAmenity, setCustomAmenity] = useState("");
  const [staffCode, setStaffCode] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingMenu, setUploadingMenu] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const selectedBar = myBars.find((b) => b.id === selectedBarId);
  const photos = selectedBar?.photos || [];
  const menuPhotos = selectedBar?.menuPhotos || [];

  const handleBarChange = (barId) => {
    setSelectedBarId(barId);
    const bar = myBars.find((b) => b.id === barId);
    setPhone(bar?.phone || "");
    setAmenities(bar?.amenities || []);
    setStaffCode(bar?.staffCode || "");
  };

  const toggleAmenity = (item) => {
    setAmenities((prev) =>
      prev.includes(item) ? prev.filter((a) => a !== item) : [...prev, item]
    );
  };

  const addCustomAmenity = () => {
    const trimmed = customAmenity.trim();
    if (!trimmed || amenities.includes(trimmed)) return;
    setAmenities((prev) => [...prev, trimmed]);
    setCustomAmenity("");
  };

  const savePhone = async () => {
    if (!selectedBarId) return;
    await updateDoc(doc(db, "bars", selectedBarId), { phone });
    await refreshUserBars();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const saveAmenities = async () => {
    if (!selectedBarId) return;
    setError("");
    try {
      await updateDoc(doc(db, "bars", selectedBarId), { amenities });
      await refreshUserBars();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error(err);
      setError("Failed to save amenities: " + err.message);
    }
  };

  const generateStaffCode = async () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setStaffCode(code);
    await updateDoc(doc(db, "bars", selectedBarId), { staffCode: code });
    await refreshUserBars();
  };

  const uploadPhoto = async (file) => {
    if (!selectedBarId || !file) return;
    setUploading(true);

    const fileRef = ref(storage, `bar-photos/${selectedBarId}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    const barRef = doc(db, "bars", selectedBarId);
    const updatedPhotos = [...photos, { url, path: fileRef.fullPath, id: Date.now() }];
    await updateDoc(barRef, { photos: updatedPhotos });
    await refreshUserBars();

    setUploading(false);
  };

  const removePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await deleteObject(ref(storage, photo.path));
    } catch (err) {
      console.error("Storage delete failed, removing from list anyway", err);
    }
    const barRef = doc(db, "bars", selectedBarId);
    const updatedPhotos = photos.filter((p) => p.id !== photo.id);
    await updateDoc(barRef, { photos: updatedPhotos });
    await refreshUserBars();
  };

  // Menu photos use their own Storage folder (bar-menus/ instead of
  // bar-photos/) and their own Firestore field (menuPhotos instead of
  // photos), so they never mix with regular bar photos, but the upload
  // and delete logic works exactly the same way.
  const uploadMenuPhoto = async (file) => {
    if (!selectedBarId || !file) return;
    setUploadingMenu(true);

    const fileRef = ref(storage, `bar-menus/${selectedBarId}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    const barRef = doc(db, "bars", selectedBarId);
    const updatedMenuPhotos = [...menuPhotos, { url, path: fileRef.fullPath, id: Date.now() }];
    await updateDoc(barRef, { menuPhotos: updatedMenuPhotos });
    await refreshUserBars();

    setUploadingMenu(false);
  };

  const removeMenuPhoto = async (photo) => {
    if (!window.confirm("Remove this menu page?")) return;
    try {
      await deleteObject(ref(storage, photo.path));
    } catch (err) {
      console.error("Storage delete failed, removing from list anyway", err);
    }
    const barRef = doc(db, "bars", selectedBarId);
    const updatedMenuPhotos = menuPhotos.filter((p) => p.id !== photo.id);
    await updateDoc(barRef, { menuPhotos: updatedMenuPhotos });
    await refreshUserBars();
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };

  const allKnownAmenities = Object.values(AMENITY_GROUPS).flat();
  // Regional amenities (Ocean View Patio, Snowmobile Trail Access) only
  // show for bars in those states. Anything already checked stays.
  const pillStates = usePillStates();
  const shownFor = (items) =>
    items.filter((item) => amenities.includes(item) || pillAllowed(item, selectedBar?.state, pillStates));

  if (myBars.length === 0) {
    return (
      <p style={{ color: "#fff" }}>
        You need to claim a bar first before you can set up its profile.
      </p>
    );
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>Bar Profile</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Show customers what your bar is actually like — photos of the crowd, the food, the space.
      </p>

      <div
        style={{
          background: "#0D0B1A",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "560px",
          marginBottom: "30px",
        }}
      >
        <label style={labelStyle}>Venue</label>
        <select
          value={selectedBarId}
          onChange={(e) => handleBarChange(e.target.value)}
          style={{ ...inputStyle, marginBottom: "18px" }}
        >
          <option value="">Select a bar</option>
          {myBars.map((bar) => (
            <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
          ))}
        </select>

        {selectedBarId && (
          <>
            <label style={labelStyle}>Phone Number</label>
            <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                style={inputStyle}
              />
              <button
                onClick={savePhone}
                style={{
                  background: saved ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
                  border: "none",
                  padding: "0 20px",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {saved ? "✓ Saved" : "Save"}
              </button>
            </div>

            <label style={labelStyle}>Staff Posting Code</label>
            <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
              Share this with your bartenders and pull-tab sellers. Your bartender or cook opens <strong>portal.barfunfinder.com/staff-post</strong> on their phone, types this code once, and can post a photo of today's food special, no login needed. Tap "New Code" if someone leaves and you want to shut their access off.
            </p>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "24px" }}>
              <div
                style={{
                  ...inputStyle,
                  background: "#16121F",
                  fontFamily: "monospace",
                  fontSize: "20px",
                  letterSpacing: "2px",
                  textAlign: "center",
                }}
              >
                {staffCode || "Not set yet"}
              </div>
              <button
                onClick={generateStaffCode}
                style={{
                  background: "linear-gradient(90deg, #6D28D9, #A855F7)",
                  border: "none",
                  padding: "12px 18px",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {staffCode ? "New Code" : "Generate Code"}
              </button>
            </div>

            <label style={labelStyle}>Amenities</label>
            <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
              Helps customers find you when searching for something specific, like darts or a patio.
            </p>

            {Object.entries(AMENITY_GROUPS).map(([groupName, items]) => (
              <div key={groupName} style={{ marginBottom: "14px" }}>
                <p style={{ fontSize: "15px", color: "#8A84A0", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {groupName}
                </p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {shownFor(items).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleAmenity(item)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: "20px",
                        border: amenities.includes(item) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                        background: amenities.includes(item) ? "rgba(168,85,247,0.15)" : "transparent",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {amenities.filter((a) => !allKnownAmenities.includes(a)).length > 0 && (
              <div style={{ marginBottom: "14px" }}>
                <p style={{ fontSize: "15px", color: "#8A84A0", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Custom
                </p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {amenities
                    .filter((a) => !allKnownAmenities.includes(a))
                    .map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleAmenity(item)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: "20px",
                          border: "2px solid #A855F7",
                          background: "rgba(168,85,247,0.15)",
                          color: "#fff",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                      >
                        {item} ×
                      </button>
                    ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              <input
                type="text"
                value={customAmenity}
                onChange={(e) => setCustomAmenity(e.target.value)}
                placeholder="Something not listed? Add it here"
                style={inputStyle}
              />
              <button
                type="button"
                onClick={addCustomAmenity}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.3)",
                  padding: "0 18px",
                  borderRadius: "8px",
                  color: "#fff",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Add
              </button>
            </div>

            <button
              onClick={saveAmenities}
              style={{
                background: saved ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
                border: "none",
                padding: "10px 18px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "12px",
              }}
            >
              {saved ? "✓ Saved" : "Save Amenities"}
            </button>
            {error && <p style={{ color: "#F472B6", marginBottom: "24px" }}>{error}</p>}

            <label style={labelStyle}>Photos</label>
            <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
              Upload as many as you'd like — food, drinks, the crowd, the space. Customers see these on your bar's page.
            </p>

            <label
              style={{
                display: "block",
                border: "1px dashed rgba(255,255,255,0.3)",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
                marginBottom: "18px",
                color: "#B8B0C8",
              }}
            >
              {uploading ? "Uploading..." : "+ Add a photo"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={uploading}
                onChange={(e) => uploadPhoto(e.target.files[0])}
              />
            </label>

            {photos.length === 0 ? (
              <p style={{ color: "#B8B0C8", marginBottom: "24px" }}>No photos uploaded yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "24px" }}>
                {photos.map((photo) => (
                  <div key={photo.id} style={{ position: "relative" }}>
                    <img
                      src={photo.url}
                      alt="Bar"
                      style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "8px", display: "block" }}
                    />
                    <button
                      onClick={() => removePhoto(photo)}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        background: "rgba(0,0,0,0.6)",
                        border: "none",
                        borderRadius: "6px",
                        color: "#fff",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label style={labelStyle}>Menu</label>
            <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
              Upload photos of each menu page. Customers can tap to zoom in on your bar's page.
            </p>

            <label
              style={{
                display: "block",
                border: "1px dashed rgba(255,255,255,0.3)",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
                marginBottom: "18px",
                color: "#B8B0C8",
              }}
            >
              {uploadingMenu ? "Uploading..." : "+ Add a menu page"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={uploadingMenu}
                onChange={(e) => uploadMenuPhoto(e.target.files[0])}
              />
            </label>

            {menuPhotos.length === 0 ? (
              <p style={{ color: "#B8B0C8" }}>No menu pages uploaded yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                {menuPhotos.map((photo) => (
                  <div key={photo.id} style={{ position: "relative" }}>
                    <img
                      src={photo.url}
                      alt="Menu page"
                      style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "8px", display: "block" }}
                    />
                    <button
                      onClick={() => removeMenuPhoto(photo)}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        background: "rgba(0,0,0,0.6)",
                        border: "none",
                        borderRadius: "6px",
                        color: "#fff",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}